import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowLeft, Save } from 'lucide-react';
import { useUIStore } from '../../../store';

const ProductEditPage = ({ sess = { uid: '', role: 'user' } }) => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    price: '',
    stock: '',
  });
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const showSnackbar = useUIStore((state) => state.showSnackbar);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch('/chatbot/api/v1.0/catalog/get', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid: sess.uid, productId }),
        });
        const result = await response.json();
        if (response.ok && result?.data) {
          setFormData({
            name: result.data.name || '',
            description: result.data.description || '',
            category: result.data.category || '',
            price: result.data.price || '',
            stock: result.data.stock || '',
          });
        } else {
          setError(result.message || 'Failed to load product');
        }
      } catch (err) {
        setError('Error loading product');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [sess.uid, productId]);

  useEffect(() => {
    if (!success) return;
    showSnackbar({ message: success, severity: 'success' });
    setSuccess('');
  }, [showSnackbar, success]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!formData.name.trim() || !formData.price) {
      setError('Name and price are required');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch('/chatbot/api/v1.0/catalog/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, uid: sess.uid, productId }),
      });
      const result = await response.json();
      if (response.ok) {
        setSuccess(result.message || 'Product updated successfully');
      } else {
        setError(result.message || 'Failed to update product');
      }
    } catch (err) {
      setError('Error updating product');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" mb={2}>
        <Button variant="outlined" startIcon={<ArrowLeft size={16} />} onClick={() => navigate('/catalog')}>
          Back
        </Button>
        <Typography variant="h5">Edit Product</Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Price"
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Stock"
                  name="stock"
                  type="number"
                  value={formData.stock}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} mt={3}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<Save size={16} />}
                disabled={submitting}
              >
                {submitting ? 'Updating...' : 'Update'}
              </Button>
              <Button variant="outlined" onClick={() => navigate('/catalog')}>
                Cancel
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProductEditPage;
